import { Controller } from "@hotwired/stimulus";
import localBootstrap from "bootstrap/dist/js/bootstrap";
import { trackUxEvent } from "../utils/ux_event_tracker";

const bootstrap = new Proxy({}, {
  get(target, prop) {
    const activeBootstrap = typeof window !== "undefined" && window.bootstrap ? window.bootstrap : localBootstrap;
    return activeBootstrap[prop];
  }
});

// Read-only Premium Showcase smart menu. Ported from ordering_layout_controller
// with all cart/checkout/modifier/offcanvas logic removed — keeps the sticky
// header (Buttons tucks away on phones), category scroll-spy, pill/sidebar nav, live search + dietary
// filters, the iOS modal scroll-lock fix, mobile bottom-sheet drag-to-dismiss, back to top, and
// a client-side item-detail modal (no server round-trip).

// How far a visitor's scroll down runs before the Buttons band tucks away. A smooth jump eases in by 1px frames
// and rounds its fractional target, so it can land a frame after it reads as landed; that pixel is not the visitor.
const TUCK_AFTER_PX = 8;

export default class extends Controller {
  static targets = [
    "mealSearch", "houseSpecialFilter", "glutenFreeFilter",
    "vegetarianFilter", "veganFilter", "stickyHeader", "noResults",
    "searchClear", "categoryUnderline", "searchInput", "searchResults", "searchHint", "menuLabel", "backToTop"
  ];

  static values = { initialMenuId: Number, searchIndexUrl: String };

  connect() {
    this.scrollLockUntil = 0;
    this.activeCategoryId = null;
    this.scrollTickPending = false;
    // Several schedules on one page (the frame) are tabs: one panel shown, the rest hidden. A
    // public menu page carries one schedule, so it scrolls as a single page.
    this.tabbed = this.element.querySelectorAll(".oc-menu-schedule-section").length > 1;
    this.lastScrollY = window.pageYOffset;
    this.scrollRun = 0;
    this.tuckable = false;
    this.jump = null;
    this.collapseMql = window.matchMedia("(max-width: 767.98px)");

    // iOS Chrome/Edge/Firefox keep a persistent opaque bottom toolbar that
    // covers the end of the embedding page. Inside the iframe neither
    // env(safe-area-inset-bottom) nor dvh can see it, so flag those browsers
    // and let the bottom-sheet clearance rules pad further (smart_menu_c.css
    // --oc-covered-bottom).
    if (/CriOS|FxiOS|EdgiOS/.test(navigator.userAgent)) {
      document.documentElement.classList.add("oc-ua-ios-toolbar");
    }

    this.initHeaderHeight();
    this.scrollListener = this.handleScroll.bind(this);
    window.addEventListener("scroll", this.scrollListener, { passive: true });
    this.initDragScroll();
    this.initScrollFade();
    this.updateScrollSpy();
    this.bindScrollLockFix();
    this.initSheetScrollDrag();
    this.initModalScrollGuard();
    this.bindSearchModal();
    this.searchIndex = null;
    if (this.hasSearchHintTarget) this.defaultSearchHint = this.searchHintTarget.textContent;

    if (this.tabbed) {
      this.activateInitialPanel();
    } else {
      // Deep-link: ?menu_id= (Stimulus value) or #menu-<schedule.id> hash. Run
      // synchronously here (header height already measured by initHeaderHeight) so
      // the first paint lands on the section instead of flashing the top first.
      this.scrollToInitialMenu();
    }
    this.scrollToInitialCategory();
    this.scrollToInitialItem();

    // Tells us whether ?menu_id=/#menu-N deep links (QR codes, marketing
    // links) are actually used. Deferred a tick: trackUxEvent drops events
    // until ux-tracking's connect sets the endpoint, and the two controllers'
    // connect order on this element isn't guaranteed.
    if (this.resolveInitialScheduleId()) {
      setTimeout(() => trackUxEvent("interaction", "deep_link", "landed"), 0);
    }
  }

  disconnect() {
    if (this.headerResizeObserver) {
      this.headerResizeObserver.disconnect();
    }
    window.removeEventListener("scroll", this.scrollListener);
    if (this.resizeListener) {
      window.removeEventListener("resize", this.resizeListener);
    }
    this.unbindScrollLockFix();
    this.destroyScrollFade();
    this.destroySheetScrollDrag();
    this.destroyModalScrollGuard();
    this.unbindSearchModal();
  }

  // --- Read-only item detail modal ---
  showItem(event) {
    // Keyboard activation (Enter/Space) — stop Space from scrolling the page.
    if (event.type === "keydown") event.preventDefault();
    this.openItemDetail(event.currentTarget);
  }

  // card is the .oc-menu-line (the clickable row), not its .oc-menu-line-wrap ancestor — it
  // carries the cloned template. Shared by the click/keyboard handler above and by search
  // activation (goToItem / the initial #item-<id> deep link), neither of which has an event.
  openItemDetail(card) {
    const template = card && card.querySelector("template.js-item-detail");
    const modal = document.getElementById("itemDetailModal");
    if (!template || !modal) return;

    const content = modal.querySelector(".js-item-detail-content");
    if (content) content.innerHTML = template.innerHTML;

    bootstrap.Modal.getOrCreateInstance(modal).show();
    queueMicrotask(() => this.clearScrollLockStyles());

    const panel = card.closest(".oc-menu-schedule-section");
    if (panel) this.dispatch("item-shown", { detail: { scheduleId: this.scheduleIdOf(panel.id) } });
  }

  // --- iOS modal scroll-lock fix ---
  bindScrollLockFix() {
    if (!this.onScrollLockShow) {
      this.clearScrollLockStyles = this.clearScrollLockStyles.bind(this);
      this.onScrollLockShow = () => {
        queueMicrotask(() => this.clearScrollLockStyles());
      };
    }

    if (!this.boundDocumentModalEvents) {
      document.addEventListener("show.bs.modal", this.onScrollLockShow);
      document.addEventListener("shown.bs.modal", this.clearScrollLockStyles);
      document.addEventListener("hide.bs.modal", this.onScrollLockShow);
      document.addEventListener("hidden.bs.modal", this.clearScrollLockStyles);
      this.boundDocumentModalEvents = true;
    }
  }

  unbindScrollLockFix() {
    if (!this.onScrollLockShow || !this.boundDocumentModalEvents) return;

    document.removeEventListener("show.bs.modal", this.onScrollLockShow);
    document.removeEventListener("shown.bs.modal", this.clearScrollLockStyles);
    document.removeEventListener("hide.bs.modal", this.onScrollLockShow);
    document.removeEventListener("hidden.bs.modal", this.clearScrollLockStyles);
    this.boundDocumentModalEvents = false;
  }

  clearScrollLockStyles() {
    // Undo Bootstrap's scrollbar compensation (the iOS layout-jump source).
    // Desktop: also drop the body overflow lock — hiding the scrollbar leaves a
    // white gutter (scrollbar-gutter: stable) and background scroll behind a
    // modal is acceptable there. Mobile sheets keep the lock; the touchmove
    // guard below handles iOS/iframe scroll chaining.
    if (!this.isMobileSheet()) {
      document.body.style.removeProperty("overflow");
    }
    document.body.style.removeProperty("padding-right");

    document.querySelectorAll(".modal.show").forEach((modal) => {
      modal.style.setProperty("padding", "0");
    });

    document.querySelectorAll(".modal.show .modal-dialog").forEach((dialog) => {
      dialog.style.removeProperty("padding-right");
      dialog.style.removeProperty("padding-left");
      dialog.style.removeProperty("margin-right");
      dialog.style.setProperty("margin-left", "auto");
      dialog.style.setProperty("margin-right", "auto");
    });

    document.querySelectorAll(
      ".sticky-top, .fixed-top, .fixed-bottom, .is-fixed, .oc-sticky-header"
    ).forEach((element) => {
      element.style.removeProperty("padding-right");
      element.style.removeProperty("margin-right");
    });
  }

  // --- Category scroller drag ---
  initDragScroll() {
    const slider = document.querySelector(".oc-categories-scroll");
    if (!slider) return;

    let isDown = false;
    let startX;
    let scrollLeft;

    slider.addEventListener("mousedown", (e) => {
      isDown = true;
      slider.classList.add("active-drag");
      startX = e.pageX - slider.offsetLeft;
      scrollLeft = slider.scrollLeft;
    });

    slider.addEventListener("mouseleave", () => {
      isDown = false;
      slider.classList.remove("active-drag");
    });

    slider.addEventListener("mouseup", () => {
      isDown = false;
      slider.classList.remove("active-drag");
    });

    slider.addEventListener("mousemove", (e) => {
      if (!isDown) return;
      e.preventDefault();
      const x = e.pageX - slider.offsetLeft;
      const walk = (x - startX) * 2;
      slider.scrollLeft = scrollLeft - walk;
    });
  }

  // --- Category scroll fade masks ---
  initScrollFade() {
    const slider = document.querySelector(".oc-categories-scroll");
    if (!slider) return;

    this.scrollFadeListener = this.updateScrollFade.bind(this);
    slider.addEventListener("scroll", this.scrollFadeListener, { passive: true });
    window.addEventListener("resize", this.scrollFadeListener, { passive: true });

    this.scrollFadeObserver = new ResizeObserver(() => {
      this.updateScrollFade();
    });
    this.scrollFadeObserver.observe(slider);

    requestAnimationFrame(() => this.updateScrollFade());
    setTimeout(() => this.updateScrollFade(), 100);

    // The web font lands after connect and widens every pill: the active pill drifts past the
    // strip's edge and the underline keeps its fallback-font offset. Settle both once it is in.
    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(() => {
        this.updateScrollFade();
        const activePill = this.visibleActivePill();
        if (activePill) this.scrollPillIntoViewIfNeeded(activePill, { smooth: false });
      });
    }
  }

  destroyScrollFade() {
    const slider = document.querySelector(".oc-categories-scroll");
    if (slider && this.scrollFadeListener) {
      slider.removeEventListener("scroll", this.scrollFadeListener);
    }
    if (this.scrollFadeListener) {
      window.removeEventListener("resize", this.scrollFadeListener);
    }
    if (this.scrollFadeObserver) {
      this.scrollFadeObserver.disconnect();
    }
  }

  updateScrollFade() {
    const slider = document.querySelector(".oc-categories-scroll");
    if (!slider) return;

    const scrollLeft = slider.scrollLeft;
    const scrollWidth = slider.scrollWidth;
    const clientWidth = slider.clientWidth;

    const tolerance = 4;
    const isAtStart = scrollLeft <= tolerance;
    const isAtEnd = (scrollLeft + clientWidth) >= (scrollWidth - tolerance);
    const canScroll = scrollWidth > clientWidth;

    if (!canScroll) {
      slider.classList.remove("mask-left", "mask-right", "mask-both");
      slider.classList.add("mask-none");
    } else if (isAtStart) {
      slider.classList.remove("mask-left", "mask-both", "mask-none");
      slider.classList.add("mask-right");
    } else if (isAtEnd) {
      slider.classList.remove("mask-right", "mask-both", "mask-none");
      slider.classList.add("mask-left");
    } else {
      slider.classList.remove("mask-left", "mask-right", "mask-none");
      slider.classList.add("mask-both");
    }

    // Look up by class, not activeCategoryId: with schedule-level pills the
    // active category id is a menu id that no pill carries.
    const activePill = this.visibleActivePill();
    if (activePill) this.updateCategoryUnderline(activePill);
  }

  // Dropdown lights a menu pill and a category chip at once, and CSS shows one of them by width (the menu
  // pills beside the desktop sidebar, the chips everywhere else). The underline and the scroll-into-view
  // follow the one on screen.
  visibleActivePill() {
    return Array.from(document.querySelectorAll(".oc-category-pill.active"))
      .find((pill) => pill.offsetParent !== null) || null;
  }

  // --- Sticky header height + tuck-away ---
  initHeaderHeight() {
    if (!this.hasStickyHeaderTarget) return;

    let lastHeight = null;
    const updateHeight = () => {
      const height = this.stickyHeaderTarget.offsetHeight;
      if (height !== lastHeight) {
        lastHeight = height;
        document.documentElement.style.setProperty("--oc-sticky-header-height", `${height}px`);
      }
      this.measureTuck();
    };

    updateHeight();

    this.headerResizeObserver = new ResizeObserver(updateHeight);
    this.headerResizeObserver.observe(this.stickyHeaderTarget);

    this.resizeListener = updateHeight;
    window.addEventListener("resize", this.resizeListener, { passive: true });
  }

  handleScroll() {
    if (this.scrollTickPending) return;
    this.scrollTickPending = true;

    requestAnimationFrame(() => {
      this.scrollTickPending = false;
      // One read of the scroll a frame, shared by the tuck-away and back to top. Programmatic scrolls (a chip
      // tap, a search jump) and the page moving under an open sheet or search are not the visitor scrolling.
      const y = this.scrollY();
      const delta = y - this.lastScrollY;
      this.lastScrollY = y;
      const jumping = this.jump !== null;
      if (jumping && this.jumpOver(y, delta)) this.jump = null;
      const byVisitor = !jumping && Date.now() >= this.scrollLockUntil &&
        !document.body.classList.contains("modal-open");
      this.scrollRun = byVisitor ? this.nextScrollRun(delta) : 0;
      this.updateTuck(y, byVisitor);
      this.updateBackToTop(y, byVisitor);
      this.updateScrollSpy();
    });
  }

  // The scroll position inside the page: iOS overscroll bounce reads past either end, which would count as a
  // turn of direction.
  scrollY() {
    return Math.min(Math.max(window.pageYOffset, 0), this.maxScrollY());
  }

  maxScrollY() {
    return Math.max(document.documentElement.scrollHeight - window.innerHeight, 0);
  }

  // Every scroll the page makes itself (a chip tap, a search jump, a deep link, back to top). A long smooth one
  // runs past scrollLockUntil, so the tuck-away and back to top wait for it to land (handleScroll).
  scrollPageTo(top, behavior) {
    const from = this.scrollY();
    const to = Math.min(Math.max(top, 0), this.maxScrollY());
    this.jump = Math.abs(to - from) < 2 ? null : { to, down: to > from, until: Date.now() + 3000 };
    window.scrollTo({ top, behavior });
  }

  // A jump is over once it lands, the visitor scrolls against it, or 3 seconds pass (a scroll that never came).
  jumpOver(y, delta) {
    const { to, down, until } = this.jump;
    if (Date.now() > until || Math.abs(y - to) < 1) return true;
    return down ? delta < 0 : delta > 0;
  }

  // How far the visitor has scrolled in the current direction: positive down, negative up.
  nextScrollRun(delta) {
    if (delta === 0) return this.scrollRun;
    return Math.sign(delta) === Math.sign(this.scrollRun) ? this.scrollRun + delta : delta;
  }

  // Buttons tucks away on phones only when its menus wrap past one row, where the band can take a quarter of
  // the screen. Re-measured whenever the band can change size (initHeaderHeight's observer and resize).
  measureTuck() {
    const pills = this.element.querySelectorAll(
      '.oc-nav--buttons .oc-categories-scroll .oc-category-pill[data-category-id^="menu_schedule"]'
    );
    this.tuckable = this.collapseMql.matches && pills.length > 1 &&
      pills[pills.length - 1].offsetTop > pills[0].offsetTop + 1;
    if (!this.tuckable) this.setTucked(false);
  }

  // The band slides up on scroll down once the page has scrolled its height past where it sticks, and comes
  // back on any scroll up. Back where it sticks (the top of the page, or a jump there) it always shows.
  updateTuck(y, byVisitor) {
    if (!this.tuckable) return;

    const header = this.stickyHeaderTarget;
    const tucked = header.classList.contains("is-tucked");
    const sticksAt = header.parentElement.getBoundingClientRect().top + window.pageYOffset - this.siteHeaderHeight();
    if (y <= sticksAt + header.offsetHeight) {
      if (tucked) this.setTucked(false);
    } else if (byVisitor && !tucked && this.scrollRun > TUCK_AFTER_PX) {
      this.setTucked(true);
    } else if (byVisitor && tucked && this.scrollRun < 0) {
      this.setTucked(false);
    }
  }

  setTucked(tucked) {
    if (this.hasStickyHeaderTarget) this.stickyHeaderTarget.classList.toggle("is-tucked", tucked);
  }

  // --- Back to top ---
  // Shows on a page over 4 screens tall once the visitor, more than 2 screens down, turns back up 40px: the
  // moment they are looking for the top. Scrolling down or reaching the first screen hides it.
  updateBackToTop(y, byVisitor) {
    if (!this.hasBackToTopTarget) return;

    const screen = window.innerHeight;
    let show = this.backToTopTarget.classList.contains("is-shown");
    if (y < screen || (byVisitor && this.scrollRun > 0)) {
      show = false;
    } else if (byVisitor && this.scrollRun <= -40 && y > 2 * screen &&
      document.documentElement.scrollHeight > 4 * screen) {
      show = true;
    }
    this.backToTopTarget.classList.toggle("is-shown", show);
  }

  prefersReducedMotion() {
    return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
  }

  // Focus moves to the nav, where a keyboard visitor picks up again, instead of staying on a button that hides.
  backToTop() {
    const smooth = !this.prefersReducedMotion();
    this.scrollLockUntil = Date.now() + 800;
    this.backToTopTarget.classList.remove("is-shown");
    this.scrollPageTo(0, smooth ? "smooth" : "instant");
    const pill = this.visibleActivePill();
    if (pill) pill.focus({ preventScroll: true });
  }

  // --- Scroll spy ---
  updateScrollSpy() {
    if (this.hasStickyHeaderTarget) {
      this.stickyHeaderTarget.classList.toggle("is-scrolled", window.pageYOffset > 0);
    }

    if (Date.now() < this.scrollLockUntil) return;

    // Track per-category sections so the sidebar/dropdown/sheet links follow the page while the
    // schedule pill stays lit (setActiveCategory maps a category to its schedule). Tabs show one
    // panel at a time, so only spy the visible panel's categories.
    const selector = this.tabbed
      ? ".oc-menu-schedule-section:not(.d-none) .scroll-margin"
      : ".scroll-margin";
    const categories = document.querySelectorAll(selector);
    if (categories.length === 0) return;

    if ((window.innerHeight + window.pageYOffset) >= document.documentElement.scrollHeight - 80) {
      const lastCategory = categories[categories.length - 1];
      this.setActiveCategory(lastCategory.id);
      return;
    }

    const referenceLine = this.hasStickyHeaderTarget
      ? this.stickyHeaderTarget.getBoundingClientRect().bottom + 8
      : 120;

    let activeCategory = null;
    let minDistance = Infinity;

    categories.forEach((category) => {
      const rect = category.getBoundingClientRect();
      if (rect.bottom <= referenceLine) return;

      const distance = Math.abs(rect.top - referenceLine);
      if (distance < minDistance) {
        minDistance = distance;
        activeCategory = category;
      }
    });

    if (activeCategory) {
      this.setActiveCategory(activeCategory.id);
    }
  }

  setActiveCategory(categoryId) {
    if (this.activeCategoryId === categoryId) return;
    this.activeCategoryId = categoryId;

    const allLinks = document.querySelectorAll(".oc-category-pill, .oc-modal-category-link, .oc-dropdown-category-link, .oc-sidebar-category-link");

    // Schedule pills carry a schedule id while the sidebar/dropdown/sheet links track categories:
    // also light up links carrying the active category's schedule id, so the schedule pill stays
    // active while scrolling through its categories.
    const sourceLink = document.querySelector(
      `[data-category-id="${categoryId}"][data-schedule-id]`
    );
    const scheduleId = categoryId.startsWith("menu_schedule")
      ? categoryId
      : sourceLink && sourceLink.dataset.scheduleId;

    let activeSidebarLink = null;

    allLinks.forEach((link) => {
      const linkId = link.dataset.categoryId;
      const isActive = linkId === categoryId || (!!scheduleId && linkId === scheduleId);
      link.classList.toggle("active", isActive);
      if (!isActive) return;
      if (link.classList.contains("oc-sidebar-category-link")) {
        activeSidebarLink = link;
      }
    });

    const activePill = this.visibleActivePill();
    if (activePill) {
      this.scrollPillIntoViewIfNeeded(activePill);
      this.updateCategoryUnderline(activePill);
    }

    if (activeSidebarLink) {
      this.scrollSidebarLinkIntoView(activeSidebarLink);
    }
  }

  scrollSidebarLinkIntoView(link) {
    const sidebar = document.querySelector(".oc-menu-sidebar");
    if (!sidebar) return;
    const sidebarRect = sidebar.getBoundingClientRect();
    const visibleTop = sidebarRect.top;
    const linkRect = link.getBoundingClientRect();
    const padding = 8;
    let delta = 0;
    if (linkRect.top < visibleTop + padding) {
      delta = linkRect.top - visibleTop - padding;
    } else if (linkRect.bottom > sidebarRect.bottom - padding) {
      delta = linkRect.bottom - sidebarRect.bottom + padding;
    }
    if (delta === 0) return;

    const smooth = !this.prefersReducedMotion();
    sidebar.scrollTo({
      top: sidebar.scrollTop + delta,
      behavior: smooth ? "smooth" : "auto",
    });
  }

  updateCategoryUnderline(activePill) {
    if (!this.hasCategoryUnderlineTarget) return;

    const left = activePill.offsetLeft;
    const width = activePill.offsetWidth;

    this.categoryUnderlineTarget.style.transform = `translateX(${left}px)`;
    this.categoryUnderlineTarget.style.width = `${width}px`;
  }

  scrollPillIntoViewIfNeeded(pill, { smooth = true } = {}) {
    const scroller = document.querySelector(".oc-categories-scroll");
    if (!scroller) return;

    const scrollerRect = scroller.getBoundingClientRect();
    const pillRect = pill.getBoundingClientRect();
    const padding = 8;
    const isVisible =
      pillRect.left >= scrollerRect.left + padding &&
      pillRect.right <= scrollerRect.right - padding;

    if (!isVisible) {
      scroller.scrollTo({
        left: Math.max(0, pill.offsetLeft + pill.offsetWidth / 2 - scroller.clientWidth / 2),
        behavior: smooth ? "smooth" : "auto"
      });
    }
  }

  // Deep-link on load. The "menu" is a schedule (an accordion group), matching
  // the old smart-menu hash contract `#menu-<schedule.id>`; ?menu_id= carries the
  // same schedule id. Resolve to that schedule's accordion group and jump to its
  // first category section (or the group itself for a PDF schedule). Instant jump
  // so an embed lands in place.
  scrollToInitialMenu() {
    const scheduleId = this.resolveInitialScheduleId();
    if (!scheduleId) return;

    const scheduleSection = document.getElementById(`menu_schedule${scheduleId}`);
    if (!scheduleSection) return;

    const firstCategory = scheduleSection.querySelector(".oc-category-section");
    const targetId = firstCategory ? firstCategory.id : scheduleSection.id;
    this.scrollToCategoryId(targetId, { smooth: false });
  }

  // A #menu<id> hash is a category link from another menu page: land on that category, under
  // the sticky header rather than behind it.
  scrollToInitialCategory() {
    const match = window.location.hash.match(/^#(menu\d+)$/);
    if (match && document.getElementById(match[1])) {
      this.scrollToCategoryId(match[1], { smooth: false });
    }
  }

  resolveInitialScheduleId() {
    if (this.initialMenuIdValue > 0) return this.initialMenuIdValue;
    const match = window.location.hash.match(/^#menu-(\d+)$/);
    return match ? Number(match[1]) : null;
  }

  // Tabs: reveal one schedule panel and its category lists (sidebar, dropdown, sheet), hide the
  // rest. The tab's data-category-id is the panel's element id (menu_schedule<id>).
  activateSchedulePanel(panelId) {
    this.element.querySelectorAll(".oc-menu-schedule-section").forEach((panel) => {
      panel.classList.toggle("d-none", panel.id !== panelId);
    });
    this.element.querySelectorAll(".oc-nav-scope").forEach((scope) => {
      scope.classList.toggle("is-active", scope.dataset.scheduleId === panelId);
    });
    if (this.hasMenuLabelTarget) this.showMenuInSwitch(panelId);
    // The nav test tracker counts a switch to another menu; the first panel shown is where the visitor landed.
    if (this.shownPanelId && this.shownPanelId !== panelId) {
      this.dispatch("schedule-shown", { detail: { scheduleId: this.scheduleIdOf(panelId) } });
    }
    this.shownPanelId = panelId;
  }

  // Dropdown: the button takes the menu's name, the strip its category chips (from the start, with no underline
  // until setActiveCategory lights one), and the sheet's Menus list marks it current.
  showMenuInSwitch(panelId) {
    const pill = this.element.querySelector(`.oc-category-pill[data-category-id="${panelId}"]`);
    if (pill) this.menuLabelTarget.textContent = pill.textContent.trim();
    this.element.querySelectorAll(".oc-category-chip").forEach((chip) => {
      chip.hidden = chip.dataset.scheduleId !== panelId;
    });
    this.element.querySelectorAll(".oc-modal-menu-link").forEach((link) => {
      if (link.dataset.categoryId === panelId) {
        link.setAttribute("aria-current", "true");
      } else {
        link.removeAttribute("aria-current");
      }
    });
    // Beside the desktop sidebar the button is hidden and the row is menu pills, which stay where they are.
    if (panelId === this.shownPanelId || this.menuLabelTarget.offsetParent === null) return;

    const strip = this.element.querySelector(".oc-categories-scroll");
    if (strip) strip.scrollLeft = 0;
    if (this.hasCategoryUnderlineTarget) this.categoryUnderlineTarget.style.width = "0px";
    this.updateScrollFade();
  }

  scheduleIdOf(panelId) {
    return Number(panelId.replace("menu_schedule", ""));
  }

  activateInitialPanel() {
    const panels = this.element.querySelectorAll(".oc-menu-schedule-section");
    if (panels.length === 0) return;

    const scheduleId = this.resolveInitialScheduleId();
    const initial =
      (scheduleId && document.getElementById(`menu_schedule${scheduleId}`)) || panels[0];

    this.activateSchedulePanel(initial.id);
    this.setActiveCategory(this.firstCategoryId(initial));
  }

  // A tab lights its first category in the sidebar, dropdown and sheet (the category's
  // data-schedule-id keeps the tab lit too). A PDF schedule has none, so the panel itself.
  firstCategoryId(panel) {
    const first = panel.querySelector(".oc-category-section");
    return first ? first.id : panel.id;
  }

  scrollToCategoryId(categoryId, { smooth = true } = {}) {
    const target = document.getElementById(categoryId);
    if (!target) return;

    const headerOffset = this.headerOffset();
    const behavior = smooth ? "smooth" : "instant";

    if (this.tabbed) {
      const panel = target.classList.contains("oc-menu-schedule-section")
        ? target
        : target.closest(".oc-menu-schedule-section");
      if (!panel) return;
      this.activateSchedulePanel(panel.id);

      if (panel === target) {
        // Tab click: the new panel starts under the sticky header. Only scroll when the page is
        // already past that point, so switching tabs near the top never moves the page.
        this.scrollLockUntil = Date.now() + 800;
        this.activeCategoryId = null;
        this.setActiveCategory(this.firstCategoryId(panel));
        const panelTop = Math.max(0, panel.getBoundingClientRect().top + window.pageYOffset - headerOffset);
        if (window.pageYOffset > panelTop) this.scrollPageTo(panelTop, behavior);
        return;
      }
      // Category link inside a tab (sidebar / dropdown / sheet): the panel is now visible, so
      // the normal offset scroll below lands on it.
    }

    this.scrollLockUntil = Date.now() + 800;
    this.activeCategoryId = null;
    this.setActiveCategory(categoryId);

    const offsetPosition = target.getBoundingClientRect().top + window.pageYOffset - headerOffset;
    this.scrollPageTo(offsetPosition, behavior);
  }

  // On a public menu page the site's header sticks above the band (see menus/_site_header).
  siteHeaderHeight() {
    return parseFloat(getComputedStyle(document.documentElement).getPropertyValue("--site-header-height")) || 0;
  }

  scrollToCategory(event) {
    const categoryId = event.currentTarget.dataset.categoryId;
    // On the public menu pages a link into another schedule is a real page link: let it navigate.
    if (!document.getElementById(categoryId)) return;
    event.preventDefault();
    this.scrollToCategoryId(categoryId);

    const dropdownToggle = event.currentTarget.closest('.dropdown')?.querySelector('[data-bs-toggle="dropdown"]');
    if (dropdownToggle) {
      const dropdown = bootstrap.Dropdown.getInstance(dropdownToggle);
      if (dropdown) dropdown.hide();
    }

    const modalElement = event.currentTarget.closest('.modal');
    if (modalElement) {
      const modal = bootstrap.Modal.getInstance(modalElement);
      if (modal) modal.hide();
    }
  }

  // --- Live Search & Filtering ---
  // Typing renders a results list (see the search-index block below); it no longer hides cards
  // in the DOM behind the modal — that stays the dietary filter's job (applyAllFilters), which
  // keeps working here with an always-empty search value.
  liveSearch(event) {
    this.toggleSearchClear(event.target.value.length > 0);
    this.renderSearchResults(event.target.value);

    // One debounce timer per input: the nav and sidebar search boxes coexist at
    // desktop widths, and a shared timer would let activity in one cancel the
    // other's pending track.
    const input = event.target;
    this.searchTrackTimers ||= new Map();
    clearTimeout(this.searchTrackTimers.get(input));
    this.searchTrackTimers.set(input, setTimeout(() => {
      if (input.value.trim().length >= 2) {
        trackUxEvent("interaction", "search", "menu_search");
      }
    }, 1200));
  }

  // --- Search modal --- (bound by hand: a Stimulus action reads "shown.bs.modal" as the event
  // "shown" with a key filter, so it never fires)
  bindSearchModal() {
    this.searchModal = document.getElementById("searchModal");
    if (!this.searchModal) return;
    this.focusSearch = this.focusSearch.bind(this);
    this.loadSearchIndex = this.loadSearchIndex.bind(this);
    this.searchModal.addEventListener("shown.bs.modal", this.focusSearch);
    this.searchModal.addEventListener("show.bs.modal", this.loadSearchIndex);
  }

  unbindSearchModal() {
    if (!this.searchModal) return;
    this.searchModal.removeEventListener("shown.bs.modal", this.focusSearch);
    this.searchModal.removeEventListener("show.bs.modal", this.loadSearchIndex);
  }

  focusSearch() {
    if (this.hasSearchInputTarget) this.searchInputTarget.focus();
  }

  closeSearch(event) {
    event.preventDefault();
    const modal = event.currentTarget.closest(".modal");
    if (modal) bootstrap.Modal.getOrCreateInstance(modal).hide();
  }

  clearSearch() {
    const inputs = document.querySelectorAll(".js-menu-search");
    inputs.forEach((input) => (input.value = ""));
    if (inputs[0]) inputs[0].focus();
    if (this.hasMealSearchTarget) this.mealSearchTarget.value = "";
    this.toggleSearchClear(false);
    this.clearSearchResultsUI();
  }

  // --- Cross-schedule search results (results list only — see class comment) ---
  // The index is its own request (up to ~90 KB for a big menu), fetched once, the first time the
  // modal opens, so a page view that never searches never pays for it. A query typed while it is
  // in flight says so and renders as soon as it lands. A failed fetch says search is unavailable
  // and forgets itself, so the next open or keystroke retries.
  loadSearchIndex() {
    if (this.searchIndex || this.searchIndexRequest || !this.searchIndexUrlValue) return;
    this.searchIndexFailed = false;
    this.searchIndexRequest = fetch(this.searchIndexUrlValue, { headers: { Accept: "application/json" } })
      .then((response) => (response.ok ? response.json() : Promise.reject(response.status)))
      .then((entries) => {
        this.searchIndex = Array.isArray(entries) ? entries : [];
      })
      .catch(() => {
        this.searchIndexFailed = true;
      })
      .finally(() => {
        this.searchIndexRequest = null;
        if (this.hasSearchInputTarget && this.searchInputTarget.value.trim().length >= 2) {
          this.renderSearchResults(this.searchInputTarget.value);
        }
      });
  }

  renderSearchResults(rawValue) {
    const query = rawValue.trim();

    if (query.length < 2) {
      this.clearSearchResultsUI();
      return;
    }

    // Until the index lands there is nothing to match against, so "No matches" would be a lie.
    if (this.searchIndex === null && this.searchIndexUrlValue) {
      if (this.hasSearchResultsTarget) {
        this.searchResultsTarget.innerHTML = "";
        this.searchResultsTarget.classList.add("d-none");
      }
      if (this.searchIndexFailed && !this.searchIndexRequest) {
        this.showSearchHint("Search is unavailable right now. Try again in a moment.");
        this.searchIndexFailed = false; // the next keystroke retries
      } else {
        this.loadSearchIndex();
        this.showSearchHint("Loading the menu…");
      }
      return;
    }

    const results = this.matchSearchIndex(query.toLowerCase()).slice(0, 40);

    if (results.length === 0) {
      if (this.hasSearchResultsTarget) {
        this.searchResultsTarget.innerHTML = "";
        this.searchResultsTarget.classList.add("d-none");
      }
      this.showSearchHint(`No matches for “${query}”.`);
      return;
    }

    this.renderSearchResultRows(results);
    if (this.hasSearchHintTarget) this.searchHintTarget.classList.add("d-none");
  }

  // Case-insensitive substring on name/description; name hits sort first. Search-index entries
  // are plain hashes (id, name, description, price, schedule: {id, name, url}, category).
  matchSearchIndex(query) {
    return (this.searchIndex || [])
      .map((item) => {
        const name = (item.name || "").toLowerCase();
        const description = (item.search_text || item.description || "").toLowerCase();
        const nameHit = name.includes(query);
        const descriptionHit = !nameHit && description.includes(query);
        if (!nameHit && !descriptionHit) return null;
        return { item, rank: nameHit ? 0 : 1 };
      })
      .filter(Boolean)
      .sort((a, b) => a.rank - b.rank)
      .map((match) => match.item);
  }

  renderSearchResultRows(results) {
    if (!this.hasSearchResultsTarget) return;

    const groups = [];
    const groupsById = new Map();
    results.forEach((item) => {
      const scheduleId = item.schedule.id;
      if (!groupsById.has(scheduleId)) {
        const group = { schedule: item.schedule, items: [] };
        groupsById.set(scheduleId, group);
        groups.push(group);
      }
      groupsById.get(scheduleId).items.push(item);
    });

    this.searchResultsTarget.innerHTML = groups.map((group) => this.renderResultGroup(group)).join("");
    this.searchResultsTarget.classList.remove("d-none");
  }

  renderResultGroup(group) {
    const rows = group.items.map((item) => this.renderResultRow(item)).join("");
    return `<div class="oc-search-results__group">
      <div class="oc-search-results__group-label">${this.escapeHtml(group.schedule.name)}</div>
      ${rows}
    </div>`;
  }

  renderResultRow(item) {
    const category = item.category ? this.escapeHtml(item.category.name) : "";
    const description = item.description ? this.escapeHtml(item.description) : "";
    const meta = [category, description].filter(Boolean).join(" · ");
    const price = item.price
      ? `<span class="oc-search-result__price">${this.escapeHtml(item.price)}</span>`
      : "";
    const url = item.schedule.url ? ` data-url="${this.escapeHtml(item.schedule.url)}"` : "";

    return `<button type="button" class="oc-search-result" data-item-id="${item.id}" data-schedule-id="${item.schedule.id}"${url} data-action="click->smart-menu-layout-c#selectSearchResult">
      <span class="oc-search-result__head">
        <span class="oc-search-result__name">${this.escapeHtml(item.name)}</span>
        ${price}
      </span>
      ${meta ? `<span class="oc-search-result__meta text-muted">${meta}</span>` : ""}
    </button>`;
  }

  clearSearchResultsUI() {
    if (this.hasSearchResultsTarget) {
      this.searchResultsTarget.innerHTML = "";
      this.searchResultsTarget.classList.add("d-none");
    }
    this.showSearchHint(this.defaultSearchHint);
  }

  showSearchHint(text) {
    if (!this.hasSearchHintTarget || text === undefined) return;
    this.searchHintTarget.textContent = text;
    this.searchHintTarget.classList.remove("d-none");
  }

  escapeHtml(value) {
    return String(value ?? "").replace(/[&<>"']/g, (char) => ({
      "&": "&amp;",
      "<": "&lt;",
      ">": "&gt;",
      '"': "&quot;",
      "'": "&#39;"
    }[char]));
  }

  // Enter in the field: jump to the first result, or close the search (nothing to activate).
  activateFirstResult(event) {
    event.preventDefault();
    const first = this.hasSearchResultsTarget ? this.searchResultsTarget.querySelector("[data-item-id]") : null;
    if (first) {
      this.activateResult(first);
    } else {
      this.closeSearch(event);
    }
  }

  selectSearchResult(event) {
    event.preventDefault();
    this.activateResult(event.currentTarget);
  }

  activateResult(row) {
    const { itemId, url } = row.dataset;
    this.goToItem(itemId, url || null);
  }

  // On the page the row came from, the card is already in the DOM: close the search sheet,
  // switch to its tab if the frame has one, scroll it under the sticky offsets and open its
  // detail modal. Otherwise (another schedule's page) navigate there with the item hash.
  goToItem(id, url) {
    const card = document.getElementById(`item-${id}`);
    if (!card) {
      if (url) window.location.assign(`${url}#item-${id}`);
      else this.hideSearchModal();
      return;
    }
    this.hideSearchModal();
    this.openCard(card);
  }

  // Switch to the card's tab (pill and underline included) and lift any dietary filter hiding
  // it: a filtered-out card sits behind an inline display:none the tab switch cannot undo, and
  // a picked result outranks the filter.
  revealCard(card) {
    const panel = card.closest(".oc-menu-schedule-section");
    if (this.tabbed && panel) this.activateSchedulePanel(panel.id);
    // Light the card's own category (pill, sidebar, underline), not just its schedule: the scroll
    // lock that follows keeps the spy from correcting it.
    const section = card.closest(".oc-category-section");
    this.activeCategoryId = null;
    this.setActiveCategory(section ? section.id : panel && panel.id);
    if (card.offsetParent === null) this.clearFilters();
  }

  hideSearchModal() {
    if (this.searchModal) bootstrap.Modal.getOrCreateInstance(this.searchModal).hide();
  }

  // Where content lands below the sticky header (plus the site header when one is mounted): the band's visible
  // height, nothing while it is tucked away.
  headerOffset() {
    if (!this.hasStickyHeaderTarget) return 180;

    const band = this.stickyHeaderTarget.classList.contains("is-tucked") ? 0 : this.stickyHeaderTarget.offsetHeight;
    return band + this.siteHeaderHeight() + 12;
  }

  scrollCardIntoView(card, { smooth = true } = {}) {
    const offsetPosition = Math.max(0, card.getBoundingClientRect().top + window.pageYOffset - this.headerOffset());
    this.scrollLockUntil = Date.now() + 800;
    this.scrollPageTo(offsetPosition, smooth ? "smooth" : "instant");
  }

  // Land on a card and open its detail: a picked search result or an #item-<id> deep link.
  openCard(card, { smooth = true } = {}) {
    this.revealCard(card);
    this.scrollCardIntoView(card, { smooth });
    const line = card.querySelector(".oc-menu-line");
    if (line) this.openItemDetail(line);
  }

  // #item-<id> deep link (a search result's URL, another page's link): land on the card and
  // open its modal once the font swap (initHeaderHeight/updateScrollFade's own wait) settles.
  scrollToInitialItem() {
    const match = window.location.hash.match(/^#item-(\d+)$/);
    if (!match) return;
    const card = document.getElementById(`item-${match[1]}`);
    if (!card) return;

    const openInitialItem = () => this.openCard(card, { smooth: false });

    if (document.fonts && document.fonts.ready) {
      document.fonts.ready.then(openInitialItem);
    } else {
      openInitialItem();
    }
  }

  applyFilters(event) {
    event.preventDefault();
    const searchValue = this.hasMealSearchTarget ? this.mealSearchTarget.value.toLowerCase() : "";
    const filters = this.getFilters();
    this.applyAllFilters(searchValue, filters);
    this.closeModal();
  }

  clearFilters() {
    if (this.hasMealSearchTarget) this.mealSearchTarget.value = "";
    document.querySelectorAll(".js-menu-search").forEach((input) => (input.value = ""));

    if (this.hasHouseSpecialFilterTarget) this.houseSpecialFilterTarget.checked = false;
    if (this.hasGlutenFreeFilterTarget) this.glutenFreeFilterTarget.checked = false;
    if (this.hasVegetarianFilterTarget) this.vegetarianFilterTarget.checked = false;
    if (this.hasVeganFilterTarget) this.veganFilterTarget.checked = false;

    this.toggleSearchClear(false);
    this.clearSearchResultsUI();
    this.applyAllFilters("", this.getFilters());
    this.closeModal();
  }

  applyAllFilters(searchValue, filters) {
    const mealItems = document.querySelectorAll('[data-online-orders-filter-target="mealItem"]');
    let visibleCount = 0;
    mealItems.forEach((mealItem) => {
      const mealAttributes = this.getMealAttributes(mealItem);
      const matches = this.matchesFilters(searchValue, filters, mealAttributes);
      mealItem.style.display = matches ? "" : "none";
      // A match on a hidden tab is not on screen, so it must not cancel "no results".
      if (matches && !mealItem.closest(".oc-menu-schedule-section.d-none")) visibleCount += 1;
    });

    this.updateFilterCount(filters, searchValue);
    this.hideEmptySchedules();
    this.toggleNoResults(visibleCount === 0);
  }

  toggleNoResults(show) {
    if (this.hasNoResultsTarget) {
      this.noResultsTarget.classList.toggle("d-none", !show);
    }
  }

  toggleSearchClear(hasValue) {
    if (this.hasSearchClearTarget) {
      this.searchClearTargets.forEach(target => {
        target.classList.toggle("d-none", !hasValue);
      });
    }
  }

  getFilters() {
    return {
      glutenFree: this.hasGlutenFreeFilterTarget ? this.glutenFreeFilterTarget.checked : false,
      vegetarian: this.hasVegetarianFilterTarget ? this.vegetarianFilterTarget.checked : false,
      vegan: this.hasVeganFilterTarget ? this.veganFilterTarget.checked : false,
      houseSpecial: this.hasHouseSpecialFilterTarget ? this.houseSpecialFilterTarget.checked : false
    };
  }

  getMealAttributes(mealItem) {
    return {
      name: mealItem.getAttribute("data-name").toLowerCase(),
      description: mealItem.getAttribute("data-description")?.toLowerCase() || "",
      glutenFree: mealItem.getAttribute("data-gluten-free") === "true",
      vegetarian: mealItem.getAttribute("data-vegetarian") === "true",
      vegan: mealItem.getAttribute("data-vegan") === "true",
      houseSpecial: mealItem.getAttribute("data-house-special") === "true"
    };
  }

  matchesFilters(searchValue, filters, mealAttributes) {
    const matchesSearch =
      mealAttributes.name.includes(searchValue) ||
      mealAttributes.description.includes(searchValue) ||
      searchValue === "";
    const matchesGlutenFree = !filters.glutenFree || mealAttributes.glutenFree;
    const matchesVegetarian = !filters.vegetarian || mealAttributes.vegetarian;
    const matchesVegan = !filters.vegan || mealAttributes.vegan;
    const matchesHouseSpecial = !filters.houseSpecial || mealAttributes.houseSpecial;

    return (
      matchesSearch &&
      matchesGlutenFree &&
      matchesVegetarian &&
      matchesVegan &&
      matchesHouseSpecial
    );
  }

  updateFilterCount(filters, searchValue) {
    const filterCountElements = document.querySelectorAll(".oc-filter-count");
    if (filterCountElements.length === 0) return;

    const filterCount =
      (filters.glutenFree ? 1 : 0) +
      (filters.vegetarian ? 1 : 0) +
      (filters.vegan ? 1 : 0) +
      (filters.houseSpecial ? 1 : 0) +
      (searchValue ? 1 : 0);

    filterCountElements.forEach((element) => {
      if (filterCount > 0) {
        element.classList.remove("d-none");
        element.textContent = filterCount;
      } else {
        element.classList.add("d-none");
        element.textContent = "";
      }
    });
  }

  closeModal() {
    const modal = document.getElementById("filterModal");
    if (modal) {
      const bootstrapModal = bootstrap.Modal.getOrCreateInstance(modal);
      if (bootstrapModal) {
        bootstrapModal.hide();
      }
    }
  }

  hideEmptySchedules() {
    this.hideEmptyContainers(".oc-menu-schedule-section");
    this.hideEmptyContainers(".oc-category-section");
  }

  hideEmptyContainers(selector) {
    const containers = document.querySelectorAll(selector);
    containers.forEach((container) => {
      const mealItems = container.querySelectorAll('[data-online-orders-filter-target="mealItem"]');
      if (mealItems.length === 0) return;
      const hasVisibleItems = Array.from(mealItems).some(
        (item) => item.style.display !== "none"
      );
      container.style.display = hasVisibleItems ? "block" : "none";
    });
  }

  // --- Bottom-sheet drag to dismiss (mobile, modals only) ---
  isMobileSheet() {
    return window.matchMedia("(max-width: 767.98px)").matches;
  }

  sheetDragStart(event) {
    if (!this.isMobileSheet()) return;
    this.draggedSheet = event.currentTarget.closest(".modal-dialog");
    if (!this.draggedSheet) return;

    this.sheetStartY = event.touches[0].clientY;
    this.sheetDelta = 0;
    this.draggedSheet.style.transition = "none";
  }

  sheetDragMove(event) {
    if (!this.draggedSheet) return;
    const delta = event.touches[0].clientY - this.sheetStartY;
    if (delta <= 0) {
      this.sheetDelta = 0;
      this.draggedSheet.style.transform = "";
      return;
    }
    this.sheetDelta = delta;
    this.draggedSheet.style.transform = `translateY(${delta}px)`;
  }

  sheetDragEnd() {
    if (!this.draggedSheet) return;
    this.settleSheet(this.draggedSheet, this.sheetDelta);
    this.draggedSheet = null;
    this.sheetDelta = 0;
  }

  // Sheet dismissals bypass data-track (no click on a marked element), so the
  // element name is derived from the modal id here.
  trackSheetDismiss(modalId) {
    const elements = {
      itemDetailModal: "item_detail_dismissed",
      filterModal: "filter_dismissed",
      fullMenuModal: "full_menu_dismissed",
      restaurantInfoModal: "info_dismissed",
    };
    if (elements[modalId]) trackUxEvent("interaction", "sheet", elements[modalId]);
  }

  settleSheet(sheet, delta) {
    const dismissed = delta > Math.min(sheet.offsetHeight * 0.25, 160);
    sheet.style.transition = "";

    if (dismissed) {
      sheet.style.transform = "translateY(100%)";
      const modal = sheet.closest(".modal");
      if (modal) {
        this.trackSheetDismiss(modal.id);
        bootstrap.Modal.getOrCreateInstance(modal).hide();
      }
      const cleanup = () => {
        sheet.style.transform = "";
        sheet.removeEventListener("transitionend", cleanup);
      };
      sheet.addEventListener("transitionend", cleanup, { once: true });
      setTimeout(cleanup, 450);
    } else {
      sheet.style.transform = "";
    }
  }

  // --- Background scroll guard (mobile, modals only) ---
  // The body overflow lock doesn't stop iOS touch scrolling (especially inside
  // an iframe): a touch on a non-scrollable part of an open sheet chains to the
  // page behind it. Block any touchmove that no scroller inside the modal can
  // consume; real scroll regions keep native scrolling, and their
  // overscroll-behavior: contain stops boundary chaining.
  initModalScrollGuard() {
    this.modalScrollGuardEls = Array.from(this.element.querySelectorAll(".modal"));
    if (!this.modalScrollGuardEls.length) return;

    this.onModalTouchMove = (event) => {
      if (!this.isMobileSheet()) return;
      const content = event.currentTarget.querySelector(".modal-content");
      const scroller = content && content.contains(event.target)
        ? this.scrollableFrom(event.target, content)
        : null;
      if (!scroller) event.preventDefault();
    };

    this.modalScrollGuardEls.forEach((el) => {
      el.addEventListener("touchmove", this.onModalTouchMove, { passive: false });
    });
  }

  destroyModalScrollGuard() {
    if (!this.modalScrollGuardEls) return;
    this.modalScrollGuardEls.forEach((el) => {
      el.removeEventListener("touchmove", this.onModalTouchMove);
    });
    this.modalScrollGuardEls = null;
  }

  // --- Pull-down-from-scroll-top to dismiss (mobile, modals only) ---
  initSheetScrollDrag() {
    this.sheetScrollers = Array.from(
      this.element.querySelectorAll(".js-sheet-scroll")
    );
    if (!this.sheetScrollers.length) return;

    this.onSheetScrollStart = this.sheetScrollDragStart.bind(this);
    this.onSheetScrollMove = this.sheetScrollDragMove.bind(this);
    this.onSheetScrollEnd = this.sheetScrollDragEnd.bind(this);

    this.sheetScrollers.forEach((el) => {
      el.addEventListener("touchstart", this.onSheetScrollStart, { passive: true });
      el.addEventListener("touchmove", this.onSheetScrollMove, { passive: false });
      el.addEventListener("touchend", this.onSheetScrollEnd, { passive: true });
    });
  }

  destroySheetScrollDrag() {
    if (!this.sheetScrollers) return;
    this.sheetScrollers.forEach((el) => {
      el.removeEventListener("touchstart", this.onSheetScrollStart);
      el.removeEventListener("touchmove", this.onSheetScrollMove);
      el.removeEventListener("touchend", this.onSheetScrollEnd);
    });
    this.sheetScrollers = null;
  }

  scrollableFrom(node, container) {
    let el = node;
    const stop = container.parentElement;
    while (el && el !== stop) {
      if (el.scrollHeight > el.clientHeight) {
        const overflowY = getComputedStyle(el).overflowY;
        if (overflowY === "auto" || overflowY === "scroll") return el;
      }
      el = el.parentElement;
    }
    return container.scrollHeight > container.clientHeight ? container : null;
  }

  sheetScrollDragStart(event) {
    if (!this.isMobileSheet()) return;
    const container = event.currentTarget;
    this.bodyDragSheet = container.closest(".modal-dialog");
    this.bodyDragScrollEl = this.scrollableFrom(event.target, container);
    this.bodyDragAtTop =
      !this.bodyDragScrollEl || this.bodyDragScrollEl.scrollTop <= 0;
    this.bodyDragStartY = event.touches[0].clientY;
    this.bodyDragDelta = 0;
    this.bodyDragEngaged = false;
  }

  sheetScrollDragMove(event) {
    if (!this.bodyDragSheet) return;
    const delta = event.touches[0].clientY - this.bodyDragStartY;

    if (!this.bodyDragEngaged) {
      const atTopNow =
        !this.bodyDragScrollEl || this.bodyDragScrollEl.scrollTop <= 0;
      if (this.bodyDragAtTop && atTopNow && delta > 0) {
        this.bodyDragEngaged = true;
        this.bodyDragSheet.style.transition = "none";
      } else {
        return;
      }
    }

    if (delta <= 0) {
      this.bodyDragDelta = 0;
      this.bodyDragSheet.style.transform = "";
      this.bodyDragSheet.style.transition = "";
      this.bodyDragEngaged = false;
      return;
    }

    event.preventDefault();
    this.bodyDragDelta = delta;
    this.bodyDragSheet.style.transform = `translateY(${delta}px)`;
  }

  sheetScrollDragEnd() {
    if (!this.bodyDragSheet) return;
    if (this.bodyDragEngaged) {
      this.settleSheet(this.bodyDragSheet, this.bodyDragDelta);
    } else {
      this.bodyDragSheet.style.transition = "";
      this.bodyDragSheet.style.transform = "";
    }
    this.bodyDragSheet = null;
    this.bodyDragScrollEl = null;
    this.bodyDragAtTop = false;
    this.bodyDragDelta = 0;
    this.bodyDragEngaged = false;
  }
}
